import { useState, useCallback, useMemo } from 'react'
import {
  CalendarDays, Clock, Calculator, Plus, Trash2, RotateCcw, Download,
  AlertTriangle, Info, Scale,
} from 'lucide-react'
import '../cdg31.css'
import Cdg31Header from '../Cdg31Header'
import { Footer } from '../Cdg31Sections'

/* ═══════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════ */

let uid = 0

function daysBetween(start, end) {
  const s = new Date(start), e = new Date(end)
  if (isNaN(s) || isNaN(e) || e < s) return null
  return Math.floor((e - s) / 86_400_000) + 1
}

function decompose(totalDays) {
  if (!totalDays || totalDays <= 0) return { years: 0, months: 0, days: 0 }
  const years = Math.floor(totalDays / 365)
  const rem = totalDays - years * 365
  const months = Math.floor(rem / 30)
  const days = rem - months * 30
  return { years, months, days }
}

function fmtDuration(d) {
  return `${d.years}a ${d.months}m ${d.days}j`
}

function detectOverlaps(rows) {
  const warnings = []
  const valid = rows
    .map((r, i) => ({ idx: i, start: new Date(r.startDate), end: new Date(r.endDate) }))
    .filter(r => !isNaN(r.start) && !isNaN(r.end) && r.end >= r.start)
  for (let i = 0; i < valid.length; i++) {
    for (let j = i + 1; j < valid.length; j++) {
      const a = valid[i], b = valid[j]
      if (a.start <= b.end && b.start <= a.end) {
        warnings.push(`Chevauchement entre la période ${a.idx + 1} et la période ${b.idx + 1}.`)
      }
    }
  }
  return warnings
}

/* ═══════════════════════════════════════════════════
   Row factories
   ═══════════════════════════════════════════════════ */

const mkTCRow = () => ({ id: ++uid, startDate: '', endDate: '' })
const mkTNCRow = () => ({ id: ++uid, startDate: '', endDate: '', hoursWorked: '', hoursRef: '35' })
const mkHeuresRow = () => ({ id: ++uid, totalHours: '', weeklyRef: '35' })

/* ═══════════════════════════════════════════════════
   Mode tabs definition
   ═══════════════════════════════════════════════════ */

const MODES = [
  {
    key: 'tc',
    label: 'Temps complet',
    sub: 'Calcul par périodes calendaires simples',
    Icon: CalendarDays,
  },
  {
    key: 'tnc',
    label: 'Temps non complet',
    sub: 'Périodes pondérées par la quotité horaire',
    Icon: Clock,
  },
  {
    key: 'heures',
    label: 'Calcul en heures',
    sub: 'Conversion d\u2019un volume horaire en ancienneté',
    Icon: Calculator,
  },
]

/* ═══════════════════════════════════════════════════
   Row components per mode
   ═══════════════════════════════════════════════════ */

function TCRow({ row, index, onChange, onRemove, canRemove }) {
  const raw = row.startDate && row.endDate ? daysBetween(row.startDate, row.endDate) : null
  const invalid = row.startDate && row.endDate && raw === null
  const dec = raw > 0 ? decompose(raw) : null

  return (
    <tr>
      <td style={{ textAlign: 'center', color: 'var(--cdg-gray-500)', fontWeight: 600 }}>{index + 1}</td>
      <td className="cell-input">
        <input type="date" className={`cdg-sim-input${invalid ? ' error' : ''}`} value={row.startDate}
          onChange={e => onChange(row.id, 'startDate', e.target.value)}
          aria-label={`Date de début, période ${index + 1}`} />
      </td>
      <td className="cell-input">
        <input type="date" className={`cdg-sim-input${invalid ? ' error' : ''}`} value={row.endDate}
          onChange={e => onChange(row.id, 'endDate', e.target.value)}
          aria-label={`Date de fin, période ${index + 1}`} />
      </td>
      <td className="cell-duration">{dec ? fmtDuration(dec) : invalid
        ? <span style={{ color: 'var(--cdg-red)', fontSize: 'var(--fs-xs)' }}>Invalide</span> : '—'}</td>
      <td className="cell-duration">{raw > 0 ? raw : '—'}</td>
      <td className="cell-duration">{dec ? fmtDuration(dec) : '—'}</td>
      <td className="cell-action">
        <button className="cdg-sim-remove-row" onClick={() => onRemove(row.id)} disabled={!canRemove}
          aria-label={`Supprimer la période ${index + 1}`}><Trash2 size={15} /></button>
      </td>
    </tr>
  )
}

function TNCRow({ row, index, onChange, onRemove, canRemove }) {
  const raw = row.startDate && row.endDate ? daysBetween(row.startDate, row.endDate) : null
  const invalid = row.startDate && row.endDate && raw === null
  const hw = parseFloat(row.hoursWorked), hr = parseFloat(row.hoursRef)
  const hasQuotity = hw > 0 && hr > 0
  const equivalent = raw > 0 && hasQuotity ? Math.round(raw * (hw / hr)) : null
  const decRetained = equivalent !== null ? decompose(equivalent) : null

  return (
    <tr>
      <td style={{ textAlign: 'center', color: 'var(--cdg-gray-500)', fontWeight: 600 }}>{index + 1}</td>
      <td className="cell-input">
        <input type="date" className={`cdg-sim-input${invalid ? ' error' : ''}`} value={row.startDate}
          onChange={e => onChange(row.id, 'startDate', e.target.value)}
          aria-label={`Date de début, période ${index + 1}`} />
      </td>
      <td className="cell-input">
        <input type="date" className={`cdg-sim-input${invalid ? ' error' : ''}`} value={row.endDate}
          onChange={e => onChange(row.id, 'endDate', e.target.value)}
          aria-label={`Date de fin, période ${index + 1}`} />
      </td>
      <td className="cell-compact">
        <input type="number" className="cdg-sim-input" placeholder="Ex : 28" min="0" max="35" step="0.5"
          value={row.hoursWorked} onChange={e => onChange(row.id, 'hoursWorked', e.target.value)}
          aria-label={`Heures hebdomadaires effectuées, période ${index + 1}`} />
      </td>
      <td className="cell-compact">
        <input type="number" className="cdg-sim-input" placeholder="35" min="1" max="35" step="0.5"
          value={row.hoursRef} onChange={e => onChange(row.id, 'hoursRef', e.target.value)}
          aria-label={`Heures hebdomadaires de référence, période ${index + 1}`} />
      </td>
      <td className="cell-duration">{raw > 0 ? raw : invalid
        ? <span style={{ color: 'var(--cdg-red)', fontSize: 'var(--fs-xs)' }}>Invalide</span> : '—'}</td>
      <td className="cell-duration">{equivalent !== null ? equivalent : '—'}</td>
      <td className="cell-duration">{decRetained ? fmtDuration(decRetained) : '—'}</td>
      <td className="cell-action">
        <button className="cdg-sim-remove-row" onClick={() => onRemove(row.id)} disabled={!canRemove}
          aria-label={`Supprimer la période ${index + 1}`}><Trash2 size={15} /></button>
      </td>
    </tr>
  )
}

function HeuresRow({ row, index, onChange, onRemove, canRemove }) {
  const hours = parseFloat(row.totalHours), ref = parseFloat(row.weeklyRef)
  const valid = hours > 0 && ref > 0
  const equivalentDays = valid ? Math.round((hours / ref) * 5) : null
  const dec = equivalentDays ? decompose(equivalentDays) : null

  return (
    <tr>
      <td style={{ textAlign: 'center', color: 'var(--cdg-gray-500)', fontWeight: 600 }}>{index + 1}</td>
      <td className="cell-input">
        <input type="number" className="cdg-sim-input" placeholder="Ex : 420" min="0" step="1"
          value={row.totalHours} onChange={e => onChange(row.id, 'totalHours', e.target.value)}
          aria-label={`Heures effectuées, ligne ${index + 1}`} />
      </td>
      <td className="cell-compact">
        <input type="number" className="cdg-sim-input" placeholder="35" min="1" max="35" step="0.5"
          value={row.weeklyRef} onChange={e => onChange(row.id, 'weeklyRef', e.target.value)}
          aria-label={`Heures hebdomadaires de référence, ligne ${index + 1}`} />
      </td>
      <td className="cell-duration">{equivalentDays ?? '—'}</td>
      <td className="cell-duration">{dec ? fmtDuration(dec) : '—'}</td>
      <td className="cell-action">
        <button className="cdg-sim-remove-row" onClick={() => onRemove(row.id)} disabled={!canRemove}
          aria-label={`Supprimer la ligne ${index + 1}`}><Trash2 size={15} /></button>
      </td>
    </tr>
  )
}

/* ═══════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════ */

export default function Cdg31SimulateurAnciennete() {
  const [mode, setMode] = useState('tc')
  const [tcRows, setTcRows] = useState([mkTCRow()])
  const [tncRows, setTncRows] = useState([mkTNCRow()])
  const [heuresRows, setHeuresRows] = useState([mkHeuresRow()])

  const rows = mode === 'tc' ? tcRows : mode === 'tnc' ? tncRows : heuresRows
  const setRows = mode === 'tc' ? setTcRows : mode === 'tnc' ? setTncRows : setHeuresRows
  const mkRow = mode === 'tc' ? mkTCRow : mode === 'tnc' ? mkTNCRow : mkHeuresRow

  const handleChange = useCallback((id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r))
  }, [setRows])

  const handleAdd = useCallback(() => setRows(prev => [...prev, mkRow()]), [setRows, mkRow])
  const handleRemove = useCallback((id) => setRows(prev => prev.filter(r => r.id !== id)), [setRows])

  const handleReset = useCallback(() => {
    uid = 0
    setTcRows([mkTCRow()])
    setTncRows([mkTNCRow()])
    setHeuresRows([mkHeuresRow()])
  }, [])

  /* ── Compute results ── */
  const { totalDays, retainedDays, dec, decRetained, overlaps, errors } = useMemo(() => {
    let rawTotal = 0, retTotal = 0
    const errs = []

    if (mode === 'tc') {
      tcRows.forEach((r, i) => {
        if (r.startDate && !r.endDate) errs.push(`Période ${i + 1} : date de fin manquante.`)
        if (!r.startDate && r.endDate) errs.push(`Période ${i + 1} : date de début manquante.`)
        if (r.startDate && r.endDate) {
          const d = daysBetween(r.startDate, r.endDate)
          if (d === null) errs.push(`Période ${i + 1} : date de fin antérieure à la date de début.`)
          else { rawTotal += d; retTotal += d }
        }
      })
    } else if (mode === 'tnc') {
      tncRows.forEach((r, i) => {
        if (r.startDate && !r.endDate) errs.push(`Période ${i + 1} : date de fin manquante.`)
        if (!r.startDate && r.endDate) errs.push(`Période ${i + 1} : date de début manquante.`)
        if (r.startDate && r.endDate) {
          const d = daysBetween(r.startDate, r.endDate)
          if (d === null) { errs.push(`Période ${i + 1} : date de fin antérieure à la date de début.`); return }
          rawTotal += d
          const hw = parseFloat(r.hoursWorked), hr = parseFloat(r.hoursRef)
          if (hw > 0 && hr > 0) retTotal += Math.round(d * (hw / hr))
          else if (!r.hoursWorked) errs.push(`Période ${i + 1} : heures hebdomadaires effectuées manquantes.`)
        }
      })
    } else {
      heuresRows.forEach((r, i) => {
        const h = parseFloat(r.totalHours), ref = parseFloat(r.weeklyRef)
        if (r.totalHours && (!h || h <= 0)) errs.push(`Ligne ${i + 1} : nombre d'heures invalide.`)
        if (h > 0 && ref > 0) {
          const eq = Math.round((h / ref) * 5)
          rawTotal += eq
          retTotal += eq
        }
      })
    }

    const ovlp = (mode === 'tc' || mode === 'tnc')
      ? detectOverlaps(mode === 'tc' ? tcRows : tncRows)
      : []

    return {
      totalDays: rawTotal,
      retainedDays: retTotal,
      dec: decompose(rawTotal),
      decRetained: decompose(retTotal),
      overlaps: ovlp,
      errors: errs,
    }
  }, [mode, tcRows, tncRows, heuresRows])

  const halfDays = mode === 'heures' ? Math.round(retainedDays / 2) : 0
  const decHalf = decompose(halfDays)

  const hasData = rows.some(r => {
    if (mode === 'heures') return r.totalHours
    return r.startDate || r.endDate
  })

  /* ── Table headers per mode ── */
  const tableHead = mode === 'tc' ? (
    <tr>
      <th style={{ width: 40 }}>#</th>
      <th>Date de début</th>
      <th>Date de fin</th>
      <th style={{ textAlign: 'center' }}>Durée</th>
      <th style={{ textAlign: 'center' }}>Jours</th>
      <th style={{ textAlign: 'center' }}>À conserver</th>
      <th style={{ width: 48 }}></th>
    </tr>
  ) : mode === 'tnc' ? (
    <tr>
      <th style={{ width: 40 }}>#</th>
      <th>Date de début</th>
      <th>Date de fin</th>
      <th>H. hebdo effectuées</th>
      <th>H. hebdo référence</th>
      <th style={{ textAlign: 'center' }}>Jours bruts</th>
      <th style={{ textAlign: 'center' }}>Jours éq. TP</th>
      <th style={{ textAlign: 'center' }}>À conserver</th>
      <th style={{ width: 48 }}></th>
    </tr>
  ) : (
    <tr>
      <th style={{ width: 40 }}>#</th>
      <th>Heures effectuées</th>
      <th>H. hebdo référence</th>
      <th style={{ textAlign: 'center' }}>Jours équivalents</th>
      <th style={{ textAlign: 'center' }}>Durée</th>
      <th style={{ width: 48 }}></th>
    </tr>
  )

  const RowComponent = mode === 'tc' ? TCRow : mode === 'tnc' ? TNCRow : HeuresRow

  return (
    <div className="cdg31-page">
      <Cdg31Header onOpenChat={() => {}} />

      <div className="cdg-sim-breadcrumb">
        <div className="cdg-container">
          <a href="/cdg31">Accueil</a>
          <span className="sep">/</span>
          <a href="/cdg31">Ressources</a>
          <span className="sep">/</span>
          <span>Simulateur de calculs d'ancienneté</span>
        </div>
      </div>

      <main className="cdg-sim-page">
        <div className="cdg-container">

          <div className="cdg-sim-header">
            <div className="cdg-sim-icon-title blue" aria-hidden="true">
              <CalendarDays size={28} />
            </div>
            <h1>Simulateur de calculs d'ancienneté</h1>
            <p>
              Calculez le total de vos services effectifs selon votre situation :
              temps complet, temps non complet ou volume horaire.
              Choisissez le mode de calcul adapté à votre cas.
            </p>
          </div>

          {/* ═══ Mode tabs ═══ */}
          <div className="cdg-sim-tabs" role="tablist" aria-label="Mode de calcul">
            {MODES.map(m => (
              <button
                key={m.key}
                role="tab"
                aria-selected={mode === m.key}
                className={`cdg-sim-tab${mode === m.key ? ' active' : ''}`}
                onClick={() => setMode(m.key)}
              >
                <m.Icon size={20} className="tab-icon" />
                {m.label}
                <span className="tab-sub">{m.sub}</span>
              </button>
            ))}
          </div>

          {/* ═══ Data table ═══ */}
          <div className="cdg-sim-card" role="tabpanel" aria-label={`Mode ${MODES.find(m => m.key === mode)?.label}`}>
            <div className="cdg-sim-card-title">
              <span className="icon-circle blue" aria-hidden="true">
                {mode === 'tc' ? <CalendarDays size={18} /> : mode === 'tnc' ? <Clock size={18} /> : <Calculator size={18} />}
              </span>
              {mode === 'tc' && 'Périodes de service — Temps complet'}
              {mode === 'tnc' && 'Périodes de service — Temps non complet'}
              {mode === 'heures' && 'Volumes horaires'}
            </div>

            {mode === 'tnc' && (
              <div className="cdg-sim-alert info" style={{ marginBottom: 'var(--space-4)' }}>
                <Info size={15} className="alert-icon" />
                <span>
                  Indiquez les heures hebdomadaires <strong>effectuées</strong> et la durée de
                  référence (généralement 35 h). La durée retenue sera pondérée par cette quotité.
                </span>
              </div>
            )}
            {mode === 'heures' && (
              <div className="cdg-sim-alert info" style={{ marginBottom: 'var(--space-4)' }}>
                <Info size={15} className="alert-icon" />
                <span>
                  Saisissez le nombre total d'heures effectuées et la durée hebdomadaire de référence.
                  Le simulateur convertit ce volume en jours équivalents temps plein (base 5 jours/semaine).
                </span>
              </div>
            )}

            <div className="cdg-sim-table-wrap">
              <table className="cdg-sim-table" role="table">
                <thead>{tableHead}</thead>
                <tbody>
                  {rows.map((r, i) => (
                    <RowComponent
                      key={r.id}
                      row={r}
                      index={i}
                      onChange={handleChange}
                      onRemove={handleRemove}
                      canRemove={rows.length > 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <button className="cdg-sim-add-row" onClick={handleAdd}
              aria-label={mode === 'heures' ? 'Ajouter une ligne' : 'Ajouter une période'}>
              <Plus size={15} /> {mode === 'heures' ? 'Ajouter une ligne' : 'Ajouter une période'}
            </button>
          </div>

          {/* ═══ Alerts ═══ */}
          {(errors.length > 0 || overlaps.length > 0) && (
            <div className="cdg-sim-alerts" role="alert">
              {errors.map((e, i) => (
                <div key={`e${i}`} className="cdg-sim-alert error">
                  <AlertTriangle size={16} className="alert-icon" />{e}
                </div>
              ))}
              {overlaps.map((w, i) => (
                <div key={`w${i}`} className="cdg-sim-alert warn">
                  <AlertTriangle size={16} className="alert-icon" />{w}
                </div>
              ))}
            </div>
          )}

          {/* ═══ Main result ═══ */}
          <div className="cdg-sim-result" role="status" aria-live="polite">
            <div className="cdg-sim-result-title">Total des services effectifs{mode === 'tnc' ? ' — durée retenue' : ''}</div>
            <div className="cdg-sim-result-grid">
              <div className="cdg-sim-result-item">
                <div className="cdg-sim-result-value">
                  {decRetained.years}<span className="unit">an{decRetained.years > 1 ? 's' : ''}</span>
                  {' '}{decRetained.months}<span className="unit">mois</span>
                  {' '}{decRetained.days}<span className="unit">jour{decRetained.days > 1 ? 's' : ''}</span>
                </div>
                <div className="cdg-sim-result-label">
                  {mode === 'tnc' ? 'Durée à conserver (équivalent temps plein)' : 'Durée consolidée à conserver'}
                </div>
              </div>
              <div className="cdg-sim-result-sep" aria-hidden="true" />
              <div className="cdg-sim-result-item">
                <div className="cdg-sim-result-value">
                  {retainedDays.toLocaleString('fr-FR')}<span className="unit">jours</span>
                </div>
                <div className="cdg-sim-result-label">
                  {mode === 'tnc' ? 'Jours équivalent temps plein' : mode === 'heures' ? 'Jours équivalents' : 'Total en jours calendaires'}
                </div>
              </div>
              {mode === 'tnc' && totalDays !== retainedDays && (
                <>
                  <div className="cdg-sim-result-sep" aria-hidden="true" />
                  <div className="cdg-sim-result-item">
                    <div className="cdg-sim-result-value">
                      {totalDays.toLocaleString('fr-FR')}<span className="unit">jours</span>
                    </div>
                    <div className="cdg-sim-result-label">Jours calendaires bruts</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ═══ Pris à moitié (mode heures only) ═══ */}
          {mode === 'heures' && retainedDays > 0 && (
            <div className="cdg-sim-result-secondary" role="status" aria-live="polite">
              <div className="sec-title">Pris à moitié</div>
              <div className="sec-value">
                {decHalf.years}<span className="unit">an{decHalf.years > 1 ? 's' : ''}</span>
                {' '}{decHalf.months}<span className="unit">mois</span>
                {' '}{decHalf.days}<span className="unit">jour{decHalf.days > 1 ? 's' : ''}</span>
              </div>
              <div className="sec-label">
                Soit {halfDays.toLocaleString('fr-FR')} jour{halfDays > 1 ? 's' : ''} —
                appliqué lorsque la réglementation prévoit une reprise à 50 %
              </div>
            </div>
          )}

          {/* ═══ Toolbar ═══ */}
          <div className="cdg-sim-toolbar">
            <button className="cdg-btn cdg-btn-outline" onClick={handleReset} disabled={!hasData}>
              <RotateCcw size={15} /> Réinitialiser
            </button>
            <a className="cdg-btn cdg-btn-primary"
              href="/assets/cdg31/simulateurs/Simulateur_calculs_anciennete_CDG31.xls" download>
              <Download size={15} /> Télécharger le fichier Excel
            </a>
          </div>

          {/* ═══ Legal ═══ */}
          <div className="cdg-sim-legal" aria-label="Références réglementaires">
            <div className="cdg-sim-legal-title"><Scale size={16} /> Références réglementaires</div>
            <p>
              Ce simulateur est fourni à titre indicatif. Le calcul définitif relève de l'autorité
              territoriale compétente.
            </p>
            <ul>
              <li>
                <strong>Décret n° 2016-596 du 12 mai 2016</strong> relatif à l'organisation des
                carrières des fonctionnaires de catégorie C de la fonction publique territoriale.
              </li>
              <li>
                Article 5-III : conditions de reprise d'ancienneté pour le classement
                lors de la nomination stagiaire.
              </li>
              <li>
                En catégorie C et B, lorsque le classement au titre de la reprise des contrats
                de droit public conduit à une rémunération inférieure à celle perçue avant la
                nomination, l'agent peut conserver à titre personnel le bénéfice d'un indice
                brut permettant le maintien de sa rémunération antérieure.
              </li>
              <li>
                Les contrats de droit privé exercés auprès d'une personne publique sont
                comptabilisés dans la catégorie « droit privé » (CES, CEC, CAE, contrat
                d'avenir, emploi jeune, apprentissage, etc.).
              </li>
            </ul>
          </div>

          <div className="cdg-sim-alerts">
            <div className="cdg-sim-alert info">
              <Info size={16} className="alert-icon" />
              <span>
                <strong>Convention de calcul POC :</strong> durée calendaire (date de fin incluse),
                conversion 365 j/an et 30 j/mois, quotité temps non complet = heures effectuées / heures de
                référence, mode heures = (heures / hebdo réf.) × 5 jours. Ces règles seront ajustées
                après validation du fichier Excel source.
              </span>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  )
}
